import Phaser from 'phaser';

interface TrailPoint {
  x: number;
  y: number;
}

/**
 * Paper.io 스타일 트레일 렌더러
 * 큐브 하단 단면과 일치하는 너비, 원형 없이 직선 끝
 */
export class TrailRenderer {
  private graphics: Phaser.GameObjects.Graphics;
  private trailPoints: TrailPoint[] = [];
  private width: number;
  private color: number;
  private minPointDistance: number = 10;
  private rainbow: boolean = false;

  constructor(scene: Phaser.Scene, width: number, color: number) {
    this.width = width;
    this.color = color;
    this.graphics = scene.add.graphics();
    this.graphics.setDepth(5);
  }

  /**
   * 파스텔 색상으로 변환
   */
  private toPastelColor(color: number): number {
    const r = (color >> 16) & 0xFF;
    const g = (color >> 8) & 0xFF;
    const b = color & 0xFF;

    const pastelR = Math.floor(r + (255 - r) * 0.35);
    const pastelG = Math.floor(g + (255 - g) * 0.35);
    const pastelB = Math.floor(b + (255 - b) * 0.35);

    return (pastelR << 16) | (pastelG << 8) | pastelB;
  }

  setColor(color: number): void {
    this.color = color;
  }

  setWidth(width: number): void {
    if (this.width === width) return;
    this.width = width;
    this.render(); // 굵기 변화를 즉시 반영 (거대화/축소)
  }

  /** 무적 무지개 꼬리 모드 */
  setRainbow(on: boolean): void {
    if (this.rainbow === on) return;
    this.rainbow = on;
    this.render();
  }

  /** 무지개일 때 매 프레임 색을 갱신하기 위해 Player.update에서 호출 */
  tickRainbow(): void {
    if (this.rainbow) this.render();
  }

  /** 렌더에 쓸 색: 무지개면 시간 기반 HSV 순환, 아니면 파스텔 */
  private getRenderColor(): number {
    if (this.rainbow) {
      const h = (Date.now() % 1500) / 1500;
      const c = Phaser.Display.Color.HSVToRGB(h, 0.85, 1) as Phaser.Display.Color;
      return c.color;
    }
    return this.toPastelColor(this.color);
  }

  addPoint(x: number, y: number, _angle: number): void {
    if (this.trailPoints.length > 0) {
      const last = this.trailPoints[this.trailPoints.length - 1];
      const dist = Math.sqrt((x - last.x) ** 2 + (y - last.y) ** 2);
      if (dist < this.minPointDistance) return;
    }

    this.trailPoints.push({ x, y });

    // 상한 안전장치. 과거 300점(약 3000px)은 부스터로 길게 그릴 때
    // 시작점 쪽 꼬리가 잘려 안 보이고, 영역 확장 폴리곤도 시작 경계와
    // 어긋나는 문제가 있었다. 맵 지름(~6000px)의 2배 이상을 커버하도록 상향.
    // (꼬리는 영역 복귀 시 리셋되므로 평소엔 수십~수백 점에 그친다)
    const MAX_TRAIL_POINTS = 1500;
    if (this.trailPoints.length > MAX_TRAIL_POINTS) {
      this.trailPoints = this.trailPoints.slice(-(MAX_TRAIL_POINTS - 100));
    }

    this.render();
  }

  private render(): void {
    this.graphics.clear();

    if (this.trailPoints.length < 2) return;

    const pastelColor = this.getRenderColor();
    const halfWidth = this.width / 2;

    // 앞뒤로 연장된 트레일 포인트 생성
    const extendedPoints = this.extendTrailPoints(30); // 30px 연장

    // 경계점 계산
    const { leftPoints, rightPoints } = this.calculateBoundaryPoints(halfWidth, extendedPoints);

    if (leftPoints.length < 2) return;

    // 메인 트레일 (반투명, 그림자 없음)
    this.drawTrailPolygon(leftPoints, rightPoints, pastelColor, 0, 0, 0.6);
  }

  /**
   * 트레일 시작점을 영역 방향으로 연장
   */
  private extendTrailPoints(extension: number): TrailPoint[] {
    if (this.trailPoints.length < 2) return [...this.trailPoints];

    const extended: TrailPoint[] = [];

    // 시작점 연장 (뒤쪽으로 - 영역 방향)
    const first = this.trailPoints[0];
    const second = this.trailPoints[1];
    const startAngle = Math.atan2(first.y - second.y, first.x - second.x);
    extended.push({
      x: first.x + Math.cos(startAngle) * extension,
      y: first.y + Math.sin(startAngle) * extension
    });

    // 모든 포인트들
    for (const point of this.trailPoints) {
      extended.push(point);
    }

    return extended;
  }

  /**
   * 경계점 계산 (부드러운 곡선)
   */
  private calculateBoundaryPoints(halfWidth: number, points?: TrailPoint[]): {
    leftPoints: { x: number; y: number }[];
    rightPoints: { x: number; y: number }[];
  } {
    const sourcePoints = points || this.trailPoints;
    const leftPoints: { x: number; y: number }[] = [];
    const rightPoints: { x: number; y: number }[] = [];

    for (let i = 0; i < sourcePoints.length; i++) {
      const point = sourcePoints[i];
      let perpAngle: number;

      if (i === 0 && sourcePoints.length > 1) {
        const next = sourcePoints[i + 1];
        perpAngle = Math.atan2(next.y - point.y, next.x - point.x) + Math.PI / 2;
      } else if (i === sourcePoints.length - 1) {
        const prev = sourcePoints[i - 1];
        perpAngle = Math.atan2(point.y - prev.y, point.x - prev.x) + Math.PI / 2;
      } else {
        const prev = sourcePoints[i - 1];
        const next = sourcePoints[i + 1];
        const angle1 = Math.atan2(point.y - prev.y, point.x - prev.x);
        const angle2 = Math.atan2(next.y - point.y, next.x - point.x);

        // 각도 차이가 180도 이상이면 조정
        let diff = angle2 - angle1;
        if (diff > Math.PI) diff -= Math.PI * 2;
        if (diff < -Math.PI) diff += Math.PI * 2;

        perpAngle = angle1 + diff / 2 + Math.PI / 2;
      }

      leftPoints.push({
        x: point.x + Math.cos(perpAngle) * halfWidth,
        y: point.y + Math.sin(perpAngle) * halfWidth,
      });

      rightPoints.push({
        x: point.x - Math.cos(perpAngle) * halfWidth,
        y: point.y - Math.sin(perpAngle) * halfWidth,
      });
    }

    return { leftPoints, rightPoints };
  }

  /**
   * 트레일 폴리곤 그리기 (원형 캡 없이)
   */
  private drawTrailPolygon(
    leftPoints: { x: number; y: number }[],
    rightPoints: { x: number; y: number }[],
    color: number,
    offsetX: number,
    offsetY: number,
    alpha: number = 1
  ): void {
    this.graphics.fillStyle(color, alpha);
    this.graphics.beginPath();

    // 왼쪽 경계 (순방향)
    this.graphics.moveTo(leftPoints[0].x + offsetX, leftPoints[0].y + offsetY);
    for (let i = 1; i < leftPoints.length; i++) {
      this.graphics.lineTo(leftPoints[i].x + offsetX, leftPoints[i].y + offsetY);
    }

    // 끝부분 직선 연결 (원형 없이)
    const lastRight = rightPoints[rightPoints.length - 1];
    this.graphics.lineTo(lastRight.x + offsetX, lastRight.y + offsetY);

    // 오른쪽 경계 (역순)
    for (let i = rightPoints.length - 2; i >= 0; i--) {
      this.graphics.lineTo(rightPoints[i].x + offsetX, rightPoints[i].y + offsetY);
    }

    // 시작부분 직선 연결 (원형 없이)
    this.graphics.lineTo(leftPoints[0].x + offsetX, leftPoints[0].y + offsetY);

    this.graphics.closePath();
    this.graphics.fillPath();
  }

  getPoints(): TrailPoint[] {
    return this.trailPoints;
  }

  setPoints(points: TrailPoint[]): void {
    this.trailPoints = points.slice();
    this.render();
  }

  getGridPoints(tileSize: number): { x: number; y: number }[] {
    const gridPoints: { x: number; y: number }[] = [];
    const seen = new Set<string>();

    for (const point of this.trailPoints) {
      const gx = Math.floor(point.x / tileSize);
      const gy = Math.floor(point.y / tileSize);
      const key = `${gx},${gy}`;

      if (!seen.has(key)) {
        seen.add(key);
        gridPoints.push({ x: gx, y: gy });
      }
    }

    return gridPoints;
  }

  clear(): void {
    this.trailPoints = [];
    this.graphics.clear();
  }

  destroy(): void {
    this.graphics.destroy();
  }
}
